import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, MapPin } from "lucide-react";
import { FadeUp, MaskLineInView } from "./motion";

const INQUIRY_TYPES = ["Speaking", "Press", "Research", "Publishing"];

const Field = ({ label, testid, children }) => (
  <label className="group block">
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-smoke transition-colors group-focus-within:text-vermilion">
      {label}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full border-b border-line bg-transparent py-3 text-base text-bone placeholder:text-smoke/60 outline-none transition-colors duration-300 focus:border-vermilion";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    toast.success("Message received.", {
      description: "Dr. Forbes' office will be in touch shortly.",
    });
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <section id="contact" className="relative border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <div className="mb-16 flex items-end justify-between gap-6">
          <FadeUp>
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-vermilion">
              05 — Correspondence
            </p>
            <h2 className="font-serif text-4xl font-light leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
              <MaskLineInView>Let&apos;s build</MaskLineInView>
              <MaskLineInView delay={0.08}>
                what&apos;s next<span className="text-vermilion">.</span>
              </MaskLineInView>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="hidden md:block">
            <p className="max-w-xs text-right font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-smoke">
              Speaking · Press · Research ·<br />
              Publishing Rights
            </p>
          </FadeUp>
        </div>

        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <FadeUp className="flex flex-col justify-between gap-12">
            <div className="space-y-8">
              <p className="max-w-md text-lg leading-relaxed text-ash">
                For speaking engagements, press inquiries, research
                collaboration, and publishing rights, reach the office of Dr.
                Nolan Ryan Forbes directly.
              </p>
              <div className="flex flex-wrap gap-3">
                {INQUIRY_TYPES.map((t) => (
                  <span
                    key={t}
                    className="border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:border-vermilion/50 hover:text-bone"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">
              <MapPin className="h-4 w-4 text-vermilion" strokeWidth={1.5} />
              Texas, United States
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="space-y-10"
            >
              <div className="grid gap-10 sm:grid-cols-2">
                <Field label="Name" testid="contact-input-name">
                  <input
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" testid="contact-input-email">
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </Field>
              </div>
              <Field label="Subject" testid="contact-input-subject">
                <input
                  data-testid="contact-input-subject"
                  required
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="Speaking engagement — spring keynote"
                  className={inputCls}
                />
              </Field>
              <Field label="Message" testid="contact-input-message">
                <textarea
                  data-testid="contact-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your event, publication, or research…"
                  className={`${inputCls} resize-none`}
                />
              </Field>
              <button
                data-testid="contact-submit-button"
                type="submit"
                className="group inline-flex w-full items-center justify-between border border-vermilion bg-vermilion px-8 py-5 font-mono text-[12px] uppercase tracking-[0.3em] text-ink transition-all duration-300 hover:bg-transparent hover:text-vermilion sm:w-auto"
              >
                Send the message
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}